<template>
  <div ref="container">
    <svg ref="hist" :height="height" :width="width">
      <g ref="main" :transform="`translate(${padding[3]}, ${padding[0]})`" class="main"></g>
      <g class="axis axis--x" :transform="`translate(${padding[3]}, ${(innerHeight + padding[0])})`"></g>
      <g class="axis axis--y" :transform="`translate(${padding[3]}, ${padding[0]})`"></g>
    </svg>
    <div ref="tooltip"></div>
  </div>
</template>

<script>
import * as tooltip from "./Tooltip"
import * as d3 from "d3"

export default {
  props: {
    height: { type: Number },
    width: { type: Number },
    padding: { type: Array },
    color: { type: String },
    chartdata: { }
  },
  computed: {
    innerHeight() {
      return this.height - (this.padding[0] + this.padding[2])
    },
    innerWidth() {
      return this.width - (this.padding[1] + this.padding[3])
    }
  },
  mounted() {
    this.draw(this.chartdata);
  },
  watch: {
   $props: {
      handler() {
        this.draw(this.chartdata);
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    draw(data) {
      if(this.width <= 0 || this.height <= 0) 
        return; //Nothing to do

      //The div which will contain tooltip information
      const tooltipDiv = d3.select(this.$refs.tooltip);
      tooltip.reinit(tooltipDiv);

      let svg = d3.select(this.$refs.hist) 

      // y scale. Enforce "0" being part of the graph
      let y = d3.scaleLinear().domain([Math.min(0, d3.min(data.bins)), Math.max(0, d3.max(data.bins))]).range([0, this.innerHeight])
      let yReverse = d3.scaleLinear().domain([Math.max(0, d3.max(data.bins)), Math.min(0, d3.min(data.bins))]).range([0, this.innerHeight])
      
      // y axis this.height
      let yAxis = d3.axisLeft(yReverse)
      svg.select(".axis--y").call(yAxis)
      
      // x scale
      let xTicks = d3.scaleLinear()
        .domain([0, data.bins.length])
        .range([0, this.innerWidth])

      // x axis
      let xAxis = d3.axisBottom(xTicks).ticks(data.bins.length)
        .tickFormat((x, i) => (i % 4 === 0 ? x * (data.range[1] - data.range[0])/data.bins.length + data.range[0] : ''))
      svg.select(".axis--x").call(xAxis)

      svg.select(".main").selectAll(".dataRect").data(data.bins)
         .join(
           enter => enter.append("rect")
             .attr("class", "dataRect")
             .attr("height", d => y(d))
             .attr("x", (d,i) => xTicks(i))
             .attr("y", (d) => this.innerHeight - y(d))
             .attr("width", this.innerWidth/data.bins.length)
             .attr("fill", this.color)
             .attr("stroke", "#ffffff"),
           update => update
             .attr("height", d => y(d))
             .attr("x", (d,i) => xTicks(i))
             .attr("y", (d) =>  this.innerHeight - y(d))
             .attr("width", this.innerWidth/data.bins.length)
             .attr("fill", this.color),
           exit => exit.remove()
      )

      let metrics = [ { value: data.mean, style: "mean" }, { value: data.median, style: "median" } ];
      let computeMetricX = (d) => (d-data.range[0]) / (data.range[1]-data.range[0]) * this.innerWidth;

      let lineWidth = 8;
      svg.select(".main").selectAll(".metric").data(metrics)
         .join(
           enter => {
             let g = enter.append("g")
               .attr("class", "metric")
               .on("mouseover", function(event, d) {
                   tooltip.mouseover(tooltipDiv, event, `${d.style}: ${d.value}`);
               })
               .on("mouseout", function() {
                   tooltip.mouseout(tooltipDiv);
               });

             g.append("line")
               .attr("x1", (d) => computeMetricX(d.value))
               .attr("x2", (d) => computeMetricX(d.value))
               .attr("y1", this.innerHeight)
               .attr("y2", 0)
               .attr("class", (d) => d.style)
               .attr("stroke", "black");

             g.append("rect")
               .attr("x", (d) => computeMetricX(d.value)-lineWidth/2)
               .attr("y", 0)
               .attr("width", lineWidth)
               .attr("height", this.innerHeight)
               .style('opacity', '0');
           },
           update => {
             update.select("rect")
               .attr("x", (d) => computeMetricX(d.value)-lineWidth/2)
               .attr("height", this.innerHeight);

             update.select("line")
               .attr("x1", (d) => computeMetricX(d.value))
               .attr("x2", (d) => computeMetricX(d.value));
           },
           exit => exit.remove()
      )
    }
  }
};
</script>
